import React, {
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ActionTakenComponent } from '@/core/components/ActionTakenComponent';
import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import {
  ActionCenterTypes,
  ActionResponseStatus,
  ErrorsFields,
  FormSubjectMessagePair,
  MatchedTargets,
  MatchTargetsPayload,
  MessagePairTypes,
  SubmitResponse,
  TakeActionFormPayload,
  TakeActionFormState,
  TakeActionWidgetProps,
  Targets,
} from '@/core/interfaces/common';

import { TakeActionFormContainer } from '@/features/NewForm/TakeActionFormContainer';

import { request } from '@/utils/request';
import { handleCreateCustomFieldsParams } from '@/utils/helpers';

import { GeneralErrorMessage } from '@/themes/globalStyles';

import { WidgetWrapper } from './TakeActionWidgetV2.styles';

export const TakeActionWidgetV2 = ({
  actionCenterDetails,
  nonGeocodedTargets,
}: TakeActionWidgetProps) => {
  const router = useRouter();
  const {
    uuid,
    hosturl,
  } = router.query;

  const {
    customFields,
    emailChannel,
    headlineConfig,
    hideSecondButton,
    isDonateButton,
    isGeocoded,
    linkDonate,
    mode,
    optIn,
    phoneChannel,
    prefilledMessages,
    messagingType,
    status,
    socialShareButtonEnabled,
    talkingPoints,
    thankYouMessage,
  } = actionCenterDetails;
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;
  const {
    messageRotation,
    messageMapping,
  } = MessagePairTypes;

  const [
    actionCenterType,
    setActionCenterType,
  ] = useState<ActionCenterTypes>(emailType);
  const [
    formState,
    setFormState,
  ] = useState<TakeActionFormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    subjectMessagePairs: [
      {
        body: [
          {
            isAllowEdit: false,
            name: '',
          },
        ],
        isAllowPersonalMessage: false,
        personalMessage: '',
        subject: {
          isAllowEdit: false,
          name: '',
        },
        uuid: '',
      },
    ],
    zipCode: '',
  });
  const [
    actionMode,
    setActionMode,
  ] = useState(ActionResponseStatus.live);
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(false);
  const [
    targets,
    setTargets,
  ] = useState<Targets>([]);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<ErrorsFields>({});
  const [
    previewTargets,
    setPreviewTargets,
  ] = useState<MatchedTargets>([]);
  const [
    currentMessageIndex,
    setCurrentMessageIndex,
  ] = useState<number>(0);

  useEffect(() => {
    if (!emailChannel && phoneChannel) {
      setActionCenterType(phoneType);
    }
  }, [
    emailChannel,
    phoneChannel,
    phoneType,
  ]);

  // The form shows a SUBMIT button at the bottom and also shows a second
  // button by default at the TOP of the message form. This is supposed to
  // enhance action taking but was at the behest of one customer and others
  // want it gone. We also don't show in message mapping because for those
  // forms the advocate does need to click through the messages and not
  // send immediately.
  const shouldShowSecondSendButton = () => (messagingType !== messageMapping) && !hideSecondButton;

  useEffect(() => {
    let subjectMessagePairs: Array<FormSubjectMessagePair> = [];

    if (prefilledMessages) {
      if (messagingType === messageMapping) {
        if (isGeocoded) {
          subjectMessagePairs = [
            {
              ...prefilledMessages[0],
              personalMessage: '',
            },
          ];
        } else {
          subjectMessagePairs = prefilledMessages.map(prefilledMessage => ({
            ...prefilledMessage,
            personalMessage: '',
          }));
        }
      } else if (messagingType === messageRotation) {
        subjectMessagePairs = [
          {
            ...prefilledMessages[Math.floor(
              Math.random() * prefilledMessages.length
            )],
            personalMessage: '',
          },
        ];
      }
    }

    const formCustomFields = customFields.map(customField => ({
      ...customField,
      value: '',
    }));

    if (!isGeocoded && nonGeocodedTargets?.length) {
      setPreviewTargets(nonGeocodedTargets);
    }

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
      subjectMessagePairs,
    }));
  }, [
    customFields,
    isGeocoded,
    prefilledMessages,
    messageMapping,
    messageRotation,
    messagingType,
    nonGeocodedTargets,
  ]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);

    const params: TakeActionFormPayload = {
      action: actionCenterType,
      contact: {
        email: formState.email,
        firstName: formState.firstName,
        isOptInEmail: formState.isOptInEmail,
        isOptInText: formState.isOptInText,
        lastName: formState.lastName,
        phoneNumber: formState.phoneNumber,
        prefix: formState.prefix,
        streetAddress: formState.streetAddress,
        zipCode: formState.zipCode,
      },
      ...(formState.customFields ?
        {
          customFields: handleCreateCustomFieldsParams(formState.customFields),
        } :
        {}),
      meta: {
        params: window.location.search,
        source: hosturl as string || window.location.href,
        type: 'widget-v2',
      },
    };

    if (actionCenterType === emailType) {
      if (messagingType === messageMapping) {
        params.messages = formState.subjectMessagePairs.map(({
          body,
          personalMessage,
          uuid: subjectMessagePairUuid,
          subject,
        }) => {
          const message = body.map(messageData => messageData.name) || [];

          return ({
            message: [
              ...message,
              ...(personalMessage ? [personalMessage] : []),
            ].join('\n\n'),
            subject: subject.name,
            targets: previewTargets
              .filter(target => target.mappedMessage === subjectMessagePairUuid)
              .map(target => target.uuid),
            uuid: subjectMessagePairUuid,
          });
        });
      } else if (messagingType === messageRotation) {
        const message = formState.subjectMessagePairs[0] ?
          formState.subjectMessagePairs[0].body.map(
            messageData => messageData.name
          ) :
          [];

        params.message = [
          ...message,
          ...(formState.subjectMessagePairs[0]?.personalMessage ?
            [formState.subjectMessagePairs[0].personalMessage] :
            []
          ),
        ].join('\n\n');
        params.messageId = formState.subjectMessagePairs[0].uuid;
        params.subject = formState.subjectMessagePairs[0].subject.name;
      }
    }

    return request
      .post<SubmitResponse>(`/public/act/${uuid}/${actionCenterType === emailType ? 'send_message' : 'make_phone_call'}/`, params)
      .then(({ data }) => {
        setTargets(data.targets || []);

        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        }

        if (data.status === ActionResponseStatus.test) {
          setActionMode(ActionResponseStatus.test);
        } else if (data.status === ActionResponseStatus.petition) {
          setActionMode(ActionResponseStatus.petition);
        }
        setIsActionTaken(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          const messages = data.messages ?
            data.messages :
            [
              {
                message: data.message,
                subject: data.subject,
              },
            ];

          if (messages[0].message || messages[0].subject) {
            setCurrentMessageIndex(0);
          }

          setError({
            ...(data.customFields ? { customFields: data.customFields } : {}),
            ...data.contact,
            messages,
          });
        }
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  const handleFetchPreviewTargets = (
    streetAddress: string | undefined,
    zipCode: string | undefined
  ) => {
    const contactStreetAddress = streetAddress || formState.streetAddress;
    const contactZipCode = zipCode || formState.zipCode;

    if (contactStreetAddress && contactZipCode && isGeocoded) {
      const params: MatchTargetsPayload = {
        streetAddress: contactStreetAddress,
        zipCode: contactZipCode,
      };

      request
        .get<MatchedTargets, MatchTargetsPayload>(`/public/act/${uuid}/match_targets/`, { params })
        .then(({ data }) => {
          setPreviewTargets(data || []);

          if (messagingType === messageMapping && data) {
            const filteredMessages = prefilledMessages?.filter(
              ({ uuid: messageUuid }) => data.some(target => target.mappedMessage === messageUuid)
            ) || [];

            setFormState(prevValues => ({
              ...prevValues,
              subjectMessagePairs: filteredMessages.map(filteredMessage => ({
                ...filteredMessage,
                personalMessage: '',
              })),
            }));
          }
        });
    }
  };

  if (isActionTaken) {
    return (
      <ActionTakenComponent
        actionCenterType={actionCenterType}
        actionMode={actionMode}
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        buttonColor={headlineConfig?.buttonColor || '#2979FF'}
        linkDonate={linkDonate}
        isDonateButton={isDonateButton}
        multipleMessages={formState.subjectMessagePairs.length > 1}
        socialShareButtonEnabled={socialShareButtonEnabled}
        submitError={error?.submitError}
        talkingPoints={talkingPoints}
        targets={targets}
        textColor={headlineConfig?.textColor || '#000'}
        thankYouMessage={thankYouMessage}
        uuid={uuid}
      />
    );
  }

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Action Center has been stopped."
      />
    );
  }

  return (
    <WidgetWrapper className="muster-wrapper">
      {error?.generalError && (
        <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
      )}
      <form
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <TakeActionFormContainer
          actionCenterType={actionCenterType}
          backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
          buttonColor={headlineConfig?.buttonColor || '#2979FF'}
          currentMessageIndex={currentMessageIndex}
          emailChannel={emailChannel}
          error={error}
          formState={formState}
          handleBlurAddress={handleFetchPreviewTargets}
          hideMessageHeadline
          isPending={isPending}
          mode={mode}
          optIn={optIn}
          phoneChannel={phoneChannel}
          previewTargets={(
            messagingType === messageMapping &&
            actionCenterType === emailType
          ) ?
            // eslint-disable-next-line max-len
            previewTargets.filter(({ mappedMessage }) => mappedMessage === formState.subjectMessagePairs[currentMessageIndex]?.uuid) :
            previewTargets
          }
          setCurrentMessageIndex={setCurrentMessageIndex}
          setError={setError}
          setFormState={setFormState}
          setActionCenterType={setActionCenterType}
          showPickActionButton
          showSecondSendButton={shouldShowSecondSendButton()}
          talkingPoints={talkingPoints}
          textColor={headlineConfig?.textColor || '#000'}
        />
      </form>
    </WidgetWrapper>
  );
};
